import { BlogArticle, BlogTakeaways } from "../BlogArticle";
import { getBlogSummary } from "../blogIndex";

const post = getBlogSummary("4")!;

const quizResults = [
  {
    title: "Metaverse Architect",
    image: "/assets/images/blog/expand-space-2024-gallery-6.jpg",
    alt: "Ahmad Danial at Expand Space 2024 after taking the future careers quiz",
    description:
      "The quiz said Metaverse Architects create immersive digital worlds, design virtual environments, and make engaging experiences for people.",
  },
  {
    title: "Roboticist",
    image: "/assets/images/blog/expand-space-2024-gallery-5.jpg",
    alt: "A future careers display from Expand Space 2024",
    description:
      "When I tried the quiz again, it suggested Roboticist: someone who builds intelligent machines that can work alongside humans.",
  },
];

const galleryImages = [
  {
    src: "/assets/images/blog/expand-space-2024-gallery-1.jpg",
    alt: "A photo from Ahmad Danial's visit to Expand Space 2024",
  },
  {
    src: "/assets/images/blog/expand-space-2024-gallery-2.jpg",
    alt: "An interactive exhibit at Expand Space 2024",
  },
  {
    src: "/assets/images/blog/expand-space-2024-gallery-3.jpg",
    alt: "A Deep Tech showcase moment at Expand Space 2024",
  },
];

export default function ExpandSpace2024() {
  return (
    <BlogArticle post={post}>
      <p>
        In November 2024, I had the opportunity to attend Expand Space, an event
        organised by Space Faculty. It was a one-day event, but it was packed
        with so many interesting things that it felt like stepping into the
        future.
      </p>

      <figure className="max-w-3xl mx-auto rounded-2xl overflow-hidden bg-white border border-stem-sand">
        <img
          src="/assets/images/blog/expand-space-2024-gallery-4.jpg"
          alt="Ahmad Danial exploring an exhibit at Expand Space 2024"
          className="w-full aspect-[16/10] object-cover object-center"
        />
        <figcaption className="px-5 py-4 text-sm text-stem-gray">
          Exploring the exhibits at Expand Space, where future technology felt
          much easier to imagine up close.
        </figcaption>
      </figure>

      <p>
        Expand Space is designed to connect youths and young professionals to
        their purpose, passion, and possibilities in Deep Tech. To me, the event
        felt like a giant playground where you could explore exciting fields
        such as Artificial Intelligence (AI), Virtual Reality (VR), Robotics,
        and Space Technology.
      </p>

      <p>
        There were lots of interactive exhibits and activities, which made
        learning about future technologies much more fun than just reading about
        them. I could walk around, try things, ask questions, and imagine what
        these technologies might become in the future.
      </p>

      <h2>Future Careers That Sounded Like Science Fiction</h2>

      <p>
        One of the highlights was the Future Careers Showcase. It featured 25
        futuristic career pathways in Deep Tech. Some of the careers sounded
        like they came straight out of a science fiction movie!
      </p>

      <p>
        There were jobs like Cybergeneticists, Metaverse Architects, and Nuclear
        Fusion Scientists. What amazed me was that some of these careers do not
        even fully exist yet, but they could become important jobs in the
        future.
      </p>

      <section className="grid gap-4 md:grid-cols-2">
        {quizResults.map((result) => (
          <article
            key={result.title}
            className="rounded-2xl bg-white border border-stem-sand overflow-hidden"
          >
            <img
              src={result.image}
              alt={result.alt}
              loading="lazy"
              className="w-full h-auto object-contain"
            />
            <div className="p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-revelBlue mb-2">
                Find Your Future Quiz
              </p>
              <h3 className="text-xl md:text-2xl font-bold text-stem-dark mb-3">
                {result.title}
              </h3>
              <p className="text-stem-charcoal text-base leading-relaxed">
                {result.description}
              </p>
            </div>
          </article>
        ))}
      </section>

      <p>
        Honestly, both careers sound really cool. I am not sure what the future
        holds for me yet. Maybe I will end up doing something that combines both
        fields, like creating virtual worlds and building robots at the same
        time!
      </p>

      <h2>The Rocket Engine Showcase</h2>

      <p>
        Another exhibit that really caught my attention was the Rocket Engine
        Development showcase by Singapore Polytechnic. As someone who is
        interested in space, this was probably my favourite part of the event.
      </p>

      <p>
        Seeing actual rocket engine components up close was surreal. Being able
        to hold and examine some of the parts made me realise how much
        engineering and innovation goes into space technology. It felt very
        different from just looking at pictures online.
      </p>

      <section className="rounded-2xl bg-white border border-stem-sand p-6 md:p-8">
        <p className="text-sm font-semibold uppercase tracking-wider text-revelBlue mb-3">
          Favourite Moment
        </p>
        <blockquote className="text-2xl md:text-3xl leading-tight text-stem-dark">
          Holding real rocket engine parts made space technology feel less like
          a faraway dream and more like something people can actually build.
        </blockquote>
      </section>

      <section className="space-y-5">
        <div>
          <h2>Moments From Expand Space</h2>
          <p>
            These photos remind me how much there was to explore in just one
            day. Some parts felt like a school learning journey, some parts felt
            like a science exhibition, and some parts felt like looking into a
            future that is still being invented.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleryImages.map((image) => (
            <figure
              key={image.src}
              className="rounded-2xl overflow-hidden bg-white border border-stem-sand"
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                className="w-full aspect-[4/5] object-cover"
              />
            </figure>
          ))}
        </div>
      </section>

      <p>
        Even though Expand Space only lasted for one day, I really enjoyed every
        moment of it. It opened my eyes to many possibilities that I had never
        thought about before.
      </p>

      <p>
        The event showed me that the future is full of exciting opportunities,
        and it made me even more curious about technology, innovation, and space
        exploration.
      </p>

      <p>Who knows? Maybe one day I will be helping to build the future too.</p>

      <BlogTakeaways
        items={[
          "Deep Tech careers can sound futuristic now, but some of them may become normal jobs later.",
          "Interactive exhibits make technology easier to understand because you can see and try things up close.",
          "The Find Your Future Quiz made me think about careers that combine imagination and engineering.",
          "Seeing rocket engine parts in person made space technology feel much more real.",
        ]}
      />
    </BlogArticle>
  );
}
